import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { systemAlertsApi } from '../../services/api';
import { reportConnectionStatus } from '../../lib/connectionStatus';
import { ShieldAlert, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';

const POLL_MS = 60_000;

/**
 * Persistent "System status" pill in the top bar. Renders ONLY when there are
 * open alerts the user is permitted to see; stays hidden when all is healthy.
 * Clicking opens the System Health page.
 */
export function SystemStatusIndicator() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await systemAlertsApi.summary();
      setSummary(res.data);
      reportConnectionStatus('system-alerts', res.data?.degraded);
    } catch (e) {
      /* silent — indicator simply won't render */
      reportConnectionStatus('system-alerts', true);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    const t = setInterval(fetchSummary, POLL_MS);
    return () => clearInterval(t);
  }, [fetchSummary]);

  if (!summary) return null;

  const total = summary.total ?? 0;
  const status = summary.status || 'ok';

  // Only surface the pill when there's something to act on. When everything is
  // healthy (green/"All clear") render nothing — keeps the top bar clean.
  if (total === 0 || status === 'ok') return null;

  const meta = {
    critical: { icon: ShieldAlert, cls: 'text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/20 hover:bg-red-500/20', count: summary.counts?.critical || 0, label: `${summary.counts?.critical || 0} critical` },
    warning: { icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20', count: total, label: `${total} issue${total === 1 ? '' : 's'}` },
    info: { icon: AlertTriangle, cls: 'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20', count: total, label: `${total} notice${total === 1 ? '' : 's'}` },
  }[status] || { icon: AlertTriangle, cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20', count: total, label: `${total} issue${total === 1 ? '' : 's'}` };

  const Icon = meta.icon;

  return (
    <button
      type="button"
      onClick={() => navigate('/integrations')}
      title={`System status: ${meta.label} — click to view`}
      aria-label={`System status: ${meta.label}`}
      data-testid="system-status-indicator"
      className={cn(
        'inline-flex items-center gap-1 h-7 px-2 rounded-full border text-xs font-semibold transition-colors',
        meta.cls
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      <span>{meta.count}</span>
    </button>
  );
}
